import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createManpowerEntry,
  createNoteEntry,
  deleteAttachment,
  deleteManpowerEntry,
  deleteNoteEntry,
  finalizeAttachment,
  getDailyLogForDate,
  requestAttachmentUploadUrl,
  updateManpowerEntry,
  updateNoteEntry,
  uploadFileToSignedUrl,
} from '../api/daily-log-api';
import type {
  AttachmentParent,
  ManpowerEntryDraft,
  NoteEntryDraft,
} from '../types';

export const dailyLogKeys = {
  all: ['daily-log'] as const,
  forDate: (projectId: number, date: string) =>
    [...dailyLogKeys.all, 'for-date', projectId, date] as const,
};

export const useDailyLogForDate = (
  projectId: number | undefined,
  date: string | undefined,
) =>
  useQuery({
    queryKey: dailyLogKeys.forDate(projectId!, date!),
    queryFn: () => getDailyLogForDate(projectId!, date!),
    enabled: !!projectId && !!date,
    staleTime: 15_000,
  });

const useInvalidateDailyLog = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [...dailyLogKeys.all] });
  };
};

export const useCreateManpowerEntry = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: ({
      dailyLogId,
      draft,
    }: {
      dailyLogId: string;
      draft: ManpowerEntryDraft;
    }) => createManpowerEntry(dailyLogId, draft),
    onSuccess: () => invalidate(),
  });
};

export const useUpdateManpowerEntry = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ManpowerEntryDraft> }) =>
      updateManpowerEntry(id, patch),
    onSuccess: () => invalidate(),
  });
};

export const useDeleteManpowerEntry = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: (id: string) => deleteManpowerEntry(id),
    onSuccess: () => invalidate(),
  });
};

export const useCreateNoteEntry = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: ({
      dailyLogId,
      draft,
    }: {
      dailyLogId: string;
      draft: NoteEntryDraft;
    }) => createNoteEntry(dailyLogId, draft),
    onSuccess: () => invalidate(),
  });
};

export const useUpdateNoteEntry = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<NoteEntryDraft> }) =>
      updateNoteEntry(id, patch),
    onSuccess: () => invalidate(),
  });
};

export const useDeleteNoteEntry = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: (id: string) => deleteNoteEntry(id),
    onSuccess: () => invalidate(),
  });
};

export const useDeleteAttachment = () => {
  const invalidate = useInvalidateDailyLog();
  return useMutation({
    mutationFn: (uuid: string) => deleteAttachment(uuid),
    onSuccess: () => invalidate(),
  });
};

export const useUploadAttachments = (projectId: number | undefined) => {
  const invalidate = useInvalidateDailyLog();

  return useMutation({
    mutationFn: async ({
      files,
      parent,
      onFileProgress,
    }: {
      files: File[];
      parent: AttachmentParent;
      onFileProgress?: (index: number, progressPct: number) => void;
    }) => {
      if (!projectId) {
        throw new Error('A project id is required before uploading.');
      }

      const uploaded = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const signed = await requestAttachmentUploadUrl({
          project_id: projectId,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
        });

        await uploadFileToSignedUrl(file, signed.signed_upload_url, (pct) =>
          onFileProgress?.(i, pct),
        );

        const attachment = await finalizeAttachment({
          project_id: projectId,
          manpower_entry_id: parent.kind === 'manpower' ? parent.id : null,
          note_entry_id: parent.kind === 'note' ? parent.id : null,
          file_uuid: signed.file_uuid,
          gcs_object_path: signed.gcs_object_path,
          original_file_name: file.name,
          mime_type: file.type || '',
          file_size: file.size,
        });
        uploaded.push(attachment);
      }
      return uploaded;
    },
    onSuccess: () => invalidate(),
  });
};
